import { webCoreTypes } from "@core/core-types.di";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { IotApiClient } from "@iot-platform/core";
import type {
    ConnectionAlertDto,
    ConnectionAlertsDto,
    ConnectionAlertUpdateBody,
} from "@station/data/dto/connection-alert.dto";
import type {
    PredefinedAlertDto,
    PredefinedAlertsDto,
    PredefinedAlertUpdateBody,
} from "@station/data/dto/predefined-alert.dto";
import type {
    SensorAlertDto,
    SensorAlertsDto,
    SensorAlertUpdateBody,
} from "@station/data/dto/sensor-alert.dto";
import { ConnectionAlertMapper } from "@station/data/mappers/connection-alert.mapper";
import { PredefinedAlertMapper } from "@station/data/mappers/predefined-sensor-alert.mapper";
import { SensorAlertMapper } from "@station/data/mappers/sensor-alert.mapper";
import type { ConnectionAlertUpdateError } from "@station/domain/errors/alert/connection-alert-update.error";
import type { PredefinedAlertUpdateError } from "@station/domain/errors/alert/predefined-alert-update.error";
import type { SensorAlertUpdateError } from "@station/domain/errors/alert/sensor-alert-update.error";
import type {
    ConnectionAlerts,
    ConnectionAlertUpdate,
} from "@station/domain/models/connection-alert.model";
import type { PredefinedAlerts } from "@station/domain/models/predefined-alert.model";
import type {
    SensorAlerts,
    SensorAlertUpdate,
} from "@station/domain/models/sensor-alert.model";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";
import { ok } from "neverthrow";

@injectable()
export class AlertDatasource {
    static readonly CONNECTION_ALERT_PATH =
        "/stations/:stationId/connection_alerts";
    static readonly VARIABLE_ALERT_PATH =
        "/stations/:stationId/variable_alerts";
    static readonly PREDEFINED_VARIABLE_ALERT_PATH =
        "/admin/stations/:stationId/predefined_variable_alerts";
    static readonly CREATED_PREDEFINED_VARIABLE_ALERT_PATH =
        "/stations/:stationId/predefined_variable_alerts";

    constructor(
        @inject(webCoreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(SensorAlertMapper)
        private readonly sensorAlertMapper: SensorAlertMapper,
        @inject(PredefinedAlertMapper)
        private readonly predefinedAlertMapper: PredefinedAlertMapper,
        @inject(ConnectionAlertMapper)
        private readonly connectionAlertMapper: ConnectionAlertMapper,
    ) {}

    fetchAllConnectionAlerts(
        stationId: string,
    ): ResultAsync<ConnectionAlerts, FallbackError> {
        return this.iotApiClient.http
            .get<ConnectionAlertsDto>(
                AlertDatasource.CONNECTION_ALERT_PATH.replace(
                    ":stationId",
                    stationId,
                ),
            )
            .mapErr(() => new FallbackError())
            .map((response) =>
                this.connectionAlertMapper.mapList(response.data),
            );
    }

    fetchAllSensorAlert(
        stationId: string,
    ): ResultAsync<SensorAlerts, FallbackError> {
        return this.iotApiClient.http
            .get<SensorAlertsDto>(
                AlertDatasource.VARIABLE_ALERT_PATH.replace(
                    ":stationId",
                    stationId,
                ),
            )
            .mapErr(() => new FallbackError())
            .map((response) => this.sensorAlertMapper.mapList(response.data));
    }

    fetchAllPredefinedAlert(
        stationId: string,
    ): ResultAsync<PredefinedAlerts, FallbackError> {
        return this.iotApiClient.http
            .get<PredefinedAlertsDto>(
                AlertDatasource.PREDEFINED_VARIABLE_ALERT_PATH.replace(
                    ":stationId",
                    stationId,
                ),
            )
            .mapErr(() => new FallbackError())
            .map((response) =>
                this.predefinedAlertMapper.mapList(response.data),
            );
    }

    fetchAllCreatedPredefinedAlert(
        stationId: string,
    ): ResultAsync<PredefinedAlerts, FallbackError> {
        return this.iotApiClient.http
            .get<PredefinedAlertsDto>(
                AlertDatasource.CREATED_PREDEFINED_VARIABLE_ALERT_PATH.replace(
                    ":stationId",
                    stationId,
                ),
            )
            .mapErr(() => new FallbackError())
            .map((response) =>
                this.predefinedAlertMapper.mapList(response.data),
            );
    }

    updateConnectionAlert(
        alertId: string,
        updateConnectionAlert: ConnectionAlertUpdate,
    ): ResultAsync<boolean, ConnectionAlertUpdateError> {
        const connectionAlertUpdateBody: ConnectionAlertUpdateBody = {
            connection_alert: {
                hours_elapsed_to_trigger:
                    updateConnectionAlert.hoursElapsedToTrigger,
                notify_all: updateConnectionAlert.notifyAll,
                station_ids: updateConnectionAlert.stationIds,
                is_enabled: updateConnectionAlert.isEnabled,
            },
        };

        return this.iotApiClient.http
            .put<ConnectionAlertDto>(
                `connection_alerts/${alertId}`,
                connectionAlertUpdateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen(() => ok(true));
    }

    updateSensorAlert(
        updateSensorAlert: SensorAlertUpdate,
    ): ResultAsync<boolean, SensorAlertUpdateError> {
        const sensorAlertUpdateBody: SensorAlertUpdateBody = {
            combined_variable_alert: {
                name: updateSensorAlert.name,
                unique_trigger: updateSensorAlert.uniqueTrigger,
                notify_all: updateSensorAlert.notifyAll,
                is_enabled: updateSensorAlert.isEnabled,
                station_ids: updateSensorAlert.stationIds,
                variable_alerts_attributes:
                    updateSensorAlert.variableAlertsAttributes.map(
                        (variableAlert) => ({
                            id: variableAlert.id,
                            variable_id: variableAlert.variableId,
                            hours_elapsed_to_trigger:
                                variableAlert.hoursElapsedToTrigger,
                            low_trigger_value:
                                variableAlert.lowTriggerValue ?? null,
                            high_trigger_value:
                                variableAlert.highTriggerValue ?? null,
                        }),
                    ),
            },
        };

        return this.iotApiClient.http
            .patch<SensorAlertDto>(
                `variable_alerts/${updateSensorAlert.id.valueOrThrow}`,
                sensorAlertUpdateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen(() => ok(true));
    }

    updatePredefinedAlert(
        stationId: string,
        predefinedAlerts: number[],
    ): ResultAsync<boolean, PredefinedAlertUpdateError> {
        const predefinedAlertUpdateBody: PredefinedAlertUpdateBody = {
            predefined_variable_alerts: predefinedAlerts,
        };

        return this.iotApiClient.http
            .patch<PredefinedAlertDto>(
                AlertDatasource.PREDEFINED_VARIABLE_ALERT_PATH.replace(
                    ":stationId",
                    stationId,
                ),
                predefinedAlertUpdateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen(() => ok(true));
    }
}
